// Request state on a bank row. Lives on the book payload, from the queue.
//
// `written` on a requested reply is false — claim never writes Salesforce — so
// the row cannot wait on a refetch gated on that flag. The click stores the
// reply onto `bank_requests`; a reload reads the same map from the queue.
//
// A create is on the queue before Fivetran lands it on `book.accounts`. Each
// request therefore carries name and domain so My requests can render that
// row without probing the door.

import type { BankRequest, BookAccount, BookPayload, MyRequest, MyRequestsPayload, WriteActionResult } from '../api/types';
import { sameEmail } from '../sidebar/permissions.ts';

export function requestForAccount(
  book: BookPayload | null | undefined,
  accountId: string,
): BankRequest | null {
  return book?.bank_requests?.[accountId] ?? null;
}

export function domainsMatch(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  if (!left || !right) return false;
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

export function namesMatch(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  if (!left || !right) return false;
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

export function matchingAccount(
  accounts: BookAccount[],
  name: string,
  domain: string,
): BookAccount | null {
  const domainHit = accounts.find(row => domainsMatch(row.domain, domain));
  if (domainHit) return domainHit;
  return accounts.find(row => namesMatch(row.account, name)) ?? null;
}

export function accountFromRequest(accountId: string, request: BankRequest): BookAccount {
  return {
    id: accountId,
    account: request.name || request.domain || accountId,
    domain: request.domain || null,
  } as BookAccount;
}

function ownerDisplayName(book: BookPayload, ownerEmail: string): string {
  // Execution filters on `sdr_owner`, the Salesforce display name. Reuse it
  // when this owner already has a row so a just-assigned account stays in scope.
  const existing = book.accounts.find(row => sameEmail(row.sdr_owner_email, ownerEmail));
  if (existing?.sdr_owner) return existing.sdr_owner;
  const local = ownerEmail.split('@')[0] || ownerEmail;
  const first = local.split('.')[0] || local;
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : ownerEmail;
}

export function applyAssignToBook(
  book: BookPayload,
  answer: WriteActionResult,
): BookPayload {
  // A dry-run or a refusal leaves the row in the bank. Only a Salesforce write
  // is enough to take it off Available before the mirror catches up.
  if (!answer.written) return book;
  const ownerEmail = answer.owner;
  if (!ownerEmail) return book;

  const ownerName = ownerDisplayName(book, ownerEmail);
  return {
    ...book,
    accounts: book.accounts.map(account => {
      const idMatch = Boolean(answer.account_id) && account.id === answer.account_id;
      const domainMatch = domainsMatch(account.domain, answer.domain);
      if (!idMatch && !domainMatch) return account;
      return {
        ...account,
        sdr_owner_email: ownerEmail,
        sdr_owner: ownerName,
        scout_state: 'assigned',
      };
    }),
  };
}

export function applyClaimToBook(
  book: BookPayload,
  answer: WriteActionResult,
  viewerEmail: string,
): BookPayload {
  if (answer.outcome !== 'requested') return book;

  const fromId = answer.account_id || '';
  const matched = book.accounts.find(row => (
    (fromId && row.id === fromId) || domainsMatch(row.domain, answer.domain)
  ));
  const accountId = fromId || matched?.id;
  if (!accountId) return book;

  const requestedBy = answer.owner || viewerEmail;
  return {
    ...book,
    bank_requests: {
      ...book.bank_requests,
      [accountId]: {
        requested_by: requestedBy,
        asked_by: viewerEmail,
        status: 'pending',
        name: answer.name || matched?.account || answer.domain,
        domain: answer.domain || matched?.domain || null,
        exclusion_reason: answer.exclusion_reason || null,
        exclusion_detail: answer.exclusion_detail || null,
        reasons: answer.reasons || null,
      },
    },
  };
}

export function applyClaimToMine(
  payload: MyRequestsPayload | undefined,
  answer: WriteActionResult,
  viewerEmail: string,
): MyRequestsPayload | undefined {
  if (!payload) return payload;
  const accountId = answer.account_id || (answer.domain ? `domain:${answer.domain}` : '');
  if (!accountId) return payload;
  if (answer.outcome === 'refused') {
    const requestedBy = answer.owner || viewerEmail;
    const next: MyRequest = {
      account_id: accountId,
      requested_by: requestedBy,
      asked_by: viewerEmail,
      status: 'couldnt',
      declined_reason: answer.message || null,
      name: answer.name || null,
      domain: answer.domain || null,
      exclusion_reason: answer.exclusion_reason || null,
      exclusion_detail: answer.exclusion_detail || null,
      reasons: answer.reasons || null,
    };
    return {
      requests: [
        next,
        ...payload.requests.filter(row => !(
          row.account_id === accountId && sameEmail(row.requested_by, requestedBy)
        )),
      ],
    };
  }
  if (answer.outcome !== 'requested') return payload;
  const requestedBy = answer.owner || viewerEmail;
  const next: MyRequest = {
    account_id: accountId,
    requested_by: requestedBy,
    asked_by: viewerEmail,
    status: 'pending',
    declined_reason: null,
    name: answer.name || null,
    domain: answer.domain || null,
    exclusion_reason: answer.exclusion_reason || null,
    exclusion_detail: answer.exclusion_detail || null,
    reasons: answer.reasons || null,
  };
  return {
    requests: [
      next,
      ...payload.requests.filter(row => !(
        row.account_id === accountId && sameEmail(row.requested_by, requestedBy)
      )),
    ],
  };
}

export function viewerAsked(request: BankRequest | null, viewerEmail: string): boolean {
  if (!request) return false;
  if (sameEmail(request.requested_by, viewerEmail)) return true;
  return sameEmail(request.asked_by, viewerEmail);
}

// What a pending request's mark says, from where the viewer stands. `requested_by` is
// whose request it is and `asked_by` is who asked; they differ when someone asked on a
// colleague's behalf, and then the owner, the asker and everyone else read it differently.
export interface RequestMark {
  label: string;
  who: string | null;
  title: string;
}

export function requestMark(
  request: BankRequest,
  viewerEmail: string,
  nameOf: (email: string) => string,
): RequestMark {
  const owner = request.requested_by;
  const asker = request.asked_by || owner;
  const onBehalf = !sameEmail(asker, owner);
  const forViewer = sameEmail(owner, viewerEmail);
  const viewerAskedIt = sameEmail(asker, viewerEmail);

  let mark: RequestMark;
  if (forViewer && !onBehalf) {
    mark = { label: 'Requested ✓', who: null, title: 'Request sent' };
  } else if (forViewer) {
    mark = {
      label: 'Requested ✓',
      who: `by ${nameOf(asker)}`,
      title: `Requested for you by ${asker}`,
    };
  } else if (viewerAskedIt) {
    mark = {
      label: 'Requested ✓',
      who: `for ${nameOf(owner)}`,
      title: `You requested this for ${owner}`,
    };
  } else if (onBehalf) {
    mark = {
      label: 'Requested',
      who: `by ${nameOf(asker)} for ${nameOf(owner)}`,
      title: `Requested by ${asker} for ${owner}`,
    };
  } else {
    mark = { label: 'Requested', who: `by ${nameOf(owner)}`, title: `Requested by ${owner}` };
  }
  return mark;
}

// The fields a flag is read from: the first code, its sentence, and every label.
export interface FlaggedFields {
  exclusion_reason?: string | null;
  exclusion_detail?: string | null;
  reasons?: string[] | null;
}

export function isFlaggedRequest(request: FlaggedFields | null | undefined): boolean {
  return Boolean(request?.exclusion_reason) || Boolean(request?.reasons?.length);
}

export function ownerFromFlagDetail(detail: string | null | undefined): string | null {
  if (!detail) return null;
  const index = detail.toLowerCase().indexOf('owner ');
  if (index < 0) return null;
  const rest = detail.slice(index + 'owner '.length);
  const untilComma = rest.split(',')[0] || rest;
  const untilDash = untilComma.split('—')[0] || untilComma;
  const cut = (untilDash.split('(')[0] || untilDash).trim();
  return cut || null;
}

// Mirror of intake.door.FLAG_SENTENCES, lowercased for an inline mark. A code
// must never reach the page: "ambiguous match" is not a sentence.
const FLAG_PHRASES: Record<string, string> = {
  recent_activity: 'someone has been active on this account recently',
  disqualified_lead: 'a disqualified lead is already on this domain',
  not_prospect: "this account's status is not Prospect",
  blank_status: 'this account has no committed status',
  has_arr: 'this account already has ARR',
  has_cs_owner: 'this account already has a customer-success owner',
  has_paid_product_org: 'this domain already has an Enterprise Cartesia org',
  // Any opportunity counts, open or closed (closed-lost included), so never "open".
  has_opportunity: 'has an opportunity',
  ambiguous_match: 'more than one Salesforce account matches this domain',
  // The person gate's reasons (GRO-161 round 1), in the gate's own words where it has them.
  owned_by_other: 'owned by another strategist',
  paying_customer: 'customer',
  paid_plan: 'has an Enterprise Cartesia plan',
  competitor: 'competitor',
  partner: 'partner',
  never_contact: 'on the never-contact list',
  not_a_company: 'not a prospect company',
  exclusion_check_unavailable: 'the exclusion check could not run — review before approving',
};

// The stage a has-opportunity sentence names, when it names one. Four shapes reach here:
// the gate's label ("has an opportunity: Closed Lost, Mar 2025, Omair Khan", GRO-235), its
// older form still on queued rows ("has an opportunity (Closed Lost)"), the door's
// open-opportunity sentence ("Open opportunity 'X' — Technical Evaluation, owner …"), and its closed one.
export function opportunityStageFromDetail(detail: string | null | undefined): string | null {
  const text = (detail || '').trim();
  if (!text) return null;

  // The gate's label, possibly inside a "Flagged: a; b." list. Stage comes first when known;
  // a month and year in first place ("Mar 2025", or an open deal's "closes Dec 2026") means it was not.
  const current = text.match(/has an opportunity: ([^,;.(]+)/i);
  const currentStage = current?.[1]?.trim();
  if (currentStage && !/^(closes )?[A-Z][a-z]{2} \d{4}$/.test(currentStage)) return currentStage;
  const labelled = text.match(/has an opportunity \(([^)]+)\)/i);
  if (labelled?.[1]) return labelled[1].trim();

  // A closed opportunity names its outcome rather than a stage.
  if (/^closed-lost\b/i.test(text)) return 'Closed Lost';
  if (/^closed-won\b/i.test(text)) return 'Closed Won';

  // An open one: the first item after the dash, unless it is the owner or the close date.
  const afterDash = text.split('—')[1];
  const first = (afterDash?.split(',')[0] || '').replace(/\(as of the last sync\)\.?/i, '').trim();
  if (!first || /^owner\s/i.test(first) || /^expected close\s/i.test(first)) return null;
  return first;
}

function opportunityPhrase(detail: string | null | undefined): string {
  const stage = opportunityStageFromDetail(detail);
  return stage ? `has an opportunity (${stage})` : 'has an opportunity';
}

export function flagReviewPhrase(
  reason: string | null | undefined,
  detail: string | null | undefined,
): string {
  const owner = ownerFromFlagDetail(detail);
  if (reason === 'has_opportunity') {
    const phrase = opportunityPhrase(detail);
    return owner ? `${phrase} with ${owner}` : phrase;
  }
  const mapped = reason ? FLAG_PHRASES[reason] : null;
  if (mapped) return mapped;
  if (detail) return detail;
  return 'flagged for review';
}

// Every reason a flag carries, as the gate's plain labels. An older request or pick has no
// `reasons`, so its single code is turned into the one phrase it always showed.
export function flagReasons(request: FlaggedFields | null | undefined): string[] {
  const labels = (request?.reasons || []).map(label => label.trim()).filter(Boolean);
  if (labels.length) return labels;
  if (!request?.exclusion_reason) return [];
  return [flagReviewPhrase(request.exclusion_reason, request.exclusion_detail)];
}

export function flagReasonsLine(request: FlaggedFields | null | undefined): string {
  return flagReasons(request).join('; ');
}

// Who an "owned by <Name>" reason names, so a force can say whom it reassigns from.
export function reassignFromName(reasons: string[] | null | undefined): string | null {
  for (const label of reasons || []) {
    const match = label.trim().match(/^owned by (.+)$/i);
    if (match?.[1]) return match[1].trim();
  }
  return null;
}

export function couldntReasonLine(reason: string | null | undefined): string {
  const text = (reason || '').trim();
  if (!text) return "Couldn't request";
  const stripped = text.replace(/^Not requested\s*[—–-]\s*/i, '').trim();
  if (!stripped || /^not requested\.?$/i.test(text)) return "Couldn't request";
  return `Couldn't request — ${stripped}`;
}

export function competingAskLine(otherCount: number): string {
  if (otherCount <= 0) return '';
  if (otherCount === 1) return '1 other asked';
  return `${otherCount} others asked`;
}

export function competingMarkLine(totalAsks: number): string {
  if (totalAsks < 2) return '';
  return `Competing · ${totalAsks} asks`;
}

export function competingCountOf(
  request: { competing_count?: number | null } | null | undefined,
): number {
  return request?.competing_count ?? 0;
}

export function approveAnywayLine(request: FlaggedFields): string {
  // Every label, and whom it reassigns from when another strategist owns it.
  const labels = (request.reasons || []).map(label => label.trim()).filter(Boolean);
  if (labels.length) {
    const from = reassignFromName(labels);
    const reassign = from ? ` This reassigns from ${from}.` : '';
    return `Approve anyway — ${labels.join('; ')}.${reassign}`;
  }

  // An older pick: one code and its sentence.
  const reason = request.exclusion_reason;
  const detail = request.exclusion_detail;
  const owner = ownerFromFlagDetail(detail);
  if (reason === 'has_opportunity' && owner) {
    return `Approve anyway — ${owner} ${opportunityPhrase(detail)} on this account.`;
  }
  if (detail) return `Approve anyway — ${detail}`;
  const mapped = reason ? FLAG_PHRASES[reason] : null;
  if (mapped) return `Approve anyway — ${mapped}.`;
  return 'Approve anyway — this account is flagged for review.';
}

export function isPendingRequest(request: BankRequest | null): boolean {
  return request?.status === 'pending';
}

export function isApprovedRequest(request: BankRequest | null): boolean {
  return request?.status === 'approved';
}

export function isCouldntRequest(request: BankRequest | null): boolean {
  return request?.status === 'couldnt';
}

export function declinedForViewer(
  request: BankRequest | null,
  viewerEmail: string,
): BankRequest | null {
  if (!request || request.status !== 'declined') return null;
  return viewerAsked(request, viewerEmail) ? request : null;
}

function rowsForViewer(
  book: BookPayload,
  viewerEmail: string,
  include: (request: BankRequest | null, viewerEmail: string, accountId: string) => boolean,
): BookAccount[] {
  const byId = new Map(book.accounts.map(row => [row.id, row]));
  const rows: BookAccount[] = [];
  const seen = new Set<string>();

  for (const row of book.accounts) {
    if (!include(requestForAccount(book, row.id), viewerEmail, row.id)) continue;
    rows.push(row);
    seen.add(row.id);
  }

  // A request whose Account the mirror has not landed yet still has to show.
  for (const [accountId, request] of Object.entries(book.bank_requests || {})) {
    if (seen.has(accountId)) continue;
    if (!include(request, viewerEmail, accountId)) continue;
    const existing = byId.get(accountId);
    rows.push(existing ?? accountFromRequest(accountId, request));
  }
  return rows;
}

// The strategist's own asks. Approvers already have the Requests band; this
// is what Available lists at the top when the viewer cannot see the queue.

export function myPendingRequests(book: BookPayload, viewerEmail: string): BookAccount[] {
  return rowsForViewer(book, viewerEmail, (request, email) => (
    isPendingRequest(request) && viewerAsked(request, email)
  ));
}

export function myDeclinedRequests(book: BookPayload, viewerEmail: string): BookAccount[] {
  return rowsForViewer(book, viewerEmail, (request, email) => (
    Boolean(declinedForViewer(request, email))
  ));
}

function stillWaitingOnBook(
  book: BookPayload,
  accountId: string,
  viewerEmail: string,
): boolean {
  const row = book.accounts.find(account => account.id === accountId);
  if (!row) return true;
  return !sameEmail(row.sdr_owner_email, viewerEmail);
}

// Approved asks that have not yet landed on the viewer's book. Once the
// overlay (or the mirror) stamps them as owner, they belong on execution.
export function myApprovedRequests(book: BookPayload, viewerEmail: string): BookAccount[] {
  return rowsForViewer(book, viewerEmail, (request, email, accountId) => (
    isApprovedRequest(request)
    && viewerAsked(request, email)
    && stillWaitingOnBook(book, accountId, email)
  ));
}

// The hover for the Flagged chip: the gate's detail and every reason it named.
export function flaggedTitle(request: FlaggedFields | null | undefined, phrase: string): string {
  const detail = request?.exclusion_detail;
  const reasons = `Flagged — ${phrase}`;
  return detail && detail !== phrase ? `${detail}\n${reasons}` : reasons;
}
